import { useState } from 'react';
import { useSnackbar } from 'notistack';
import apiClient from '../apiClient';
import { useExportJobs } from '../hooks/useExportJobs';
import MlModalForm from './MlModalForm';
import BandFilterModal from './BandFilterModal';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface DownloadCsvProps {
  selectedRows?: string[];
  campaignId?: string;
  selectedNetworkType: string;
  styleOverrides?: React.CSSProperties;
  disabled?: boolean;
  bandName?: string[];
}

export default function DownloadCsv({
  selectedRows,
  campaignId,
  selectedNetworkType,
  styleOverrides,
  disabled,
  bandName = [],
}: DownloadCsvProps) {
  const [isLoading, setLoading] = useState(false);
  const [showMlModal, setShowMlModal] = useState(false);
  const [showBandModal, setShowBandModal] = useState(false);
  const [bandModalExportType, setBandModalExportType] = useState<'cellular_csv' | 'wifi_csv'>('cellular_csv');
  const { enqueueSnackbar } = useSnackbar();
  const { watchJob, invalidateExportJobs } = useExportJobs();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const validateSelection = () => {
    if ((!selectedRows || selectedRows.length === 0) && !campaignId) {
      enqueueSnackbar('Please select a dataset or enter a campaign ID to download CSV', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return false;
    }
    return true;
  };

  const handleAsyncDownload = async (
    e: React.MouseEvent,
    exportType: string,
  ) => {
    e.preventDefault();
    if (!validateSelection()) return;
    try {
      setLoading(true);
      const { data } = await apiClient.post<{ job_id?: string }>(
        '/sigcap/api/request-collection-download/',
        {
          collection_ids: selectedRows,
          export_type: exportType,
        },
      );
      if (data.job_id) {
        watchJob(data.job_id);
      }
      invalidateExportJobs();
      enqueueSnackbar(
        'Export started. Track progress under Download Status in your account menu.',
        {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        },
      );
    } catch (error) {
      enqueueSnackbar('Error submitting download request', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBandFilteredDownload = async (exportType: string, bandFilter: string[]) => {
    if (!validateSelection()) return;
    try {
      setLoading(true);
      const { data } = await apiClient.post<{ job_id?: string }>(
        '/sigcap/api/request-collection-download/',
        {
          collection_ids: selectedRows,
          export_type: exportType,
          band_filter: bandFilter,
        },
      );
      if (data.job_id) {
        watchJob(data.job_id);
      }
      invalidateExportJobs();
      enqueueSnackbar(
        'Export started. Track progress under Download Status in your account menu.',
        {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        },
      );
    } catch (error) {
      enqueueSnackbar('Error submitting download request', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } finally {
      setLoading(false);
    }
  };

  const openBandModal = (exportType: 'cellular_csv' | 'wifi_csv') => {
    setBandModalExportType(exportType);
    setShowBandModal(true);
    handleMenuClose();
  };

  return (
    <>
      <Button
        variant="contained"
        style={styleOverrides}
        disabled={isLoading || disabled}
        onClick={handleMenuOpen}
        endIcon={<KeyboardArrowDownIcon />}
      >
        {isLoading ? 'Requesting…' : 'Download CSV'}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={(e) => { handleAsyncDownload(e, 'general_csv'); handleMenuClose(); }}>General CSV</MenuItem>
        <MenuItem onClick={() => openBandModal('cellular_csv')}>Cellular CSV</MenuItem>
        <MenuItem onClick={() => openBandModal('wifi_csv')}>Wi-Fi CSV</MenuItem>
        <MenuItem onClick={(e) => { handleAsyncDownload(e, 'bt_csv'); handleMenuClose(); }}>Bluetooth CSV</MenuItem>
        <MenuItem onClick={(e) => { handleAsyncDownload(e, 'gps_csv'); handleMenuClose(); }}>GPS CSV</MenuItem>
        <MenuItem onClick={() => { setShowMlModal(true); handleMenuClose(); }}>ML CSV</MenuItem>
      </Menu>
      <BandFilterModal
        show={showBandModal}
        setShow={setShowBandModal}
        exportType={bandModalExportType}
        initialBandFilters={bandName}
        onSubmit={handleBandFilteredDownload}
      />
      <MlModalForm
        showMlModal={showMlModal}
        setShowMlModal={setShowMlModal}
        selectedRows={selectedRows || []}
        handleMlClick={handleAsyncDownload}
      />
    </>
  );
}
